import type { GameSummary } from '@cardengine/shared';
import { linkProps } from '../router.js';

interface Variant {
  name: string;
  body: string;
}

const VARIANTS: Variant[] = [
  {
    name: 'Three in a row',
    body: 'Nine squares, three in a row, exactly as it is played on the back of an envelope. Two people who know what they are doing will draw every time — so this is really a game about whether the other one is paying attention.',
  },
  {
    name: 'Four by four',
    body: 'Sixteen squares, and still only three in a row to win. Suddenly there is far too much board to defend: threats appear diagonally from nowhere and blocking one opens another. The draws stop almost immediately.',
  },
  {
    name: 'Avoid the line',
    body: 'Misère — make three in a row and you lose. Everything you know is backwards, the squares you want are the ones nobody else wants, and the board filling up is a countdown rather than a stalemate.',
  },
];

export function NoughtsHowToPlay({ game }: { game: GameSummary }) {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">How to play</p>
        <h1 className="prose__title">{game.name}</h1>
        <p className="prose__lead">
          Take turns claiming squares. Get your mark in a line — across, down or diagonally — before
          the other one does.
        </p>
      </header>

      <section className="prose__section">
        <h2>A go</h2>
        <p>
          Crosses go first. Click any empty square to claim it, and the turn passes. There is nothing
          to draw, nothing to hold and nothing hidden — both of you are looking at exactly the same
          board, which is what makes losing so annoying.
        </p>
        <p>
          The game ends the moment somebody completes a line, or when the last square is filled and
          nobody has. A full grid with no line is a <strong>draw</strong>, and a draw is a real
          result here rather than a failure — it is what correct play produces.
        </p>
      </section>

      <section className="prose__section">
        <h2>Three ways to play it</h2>
        <ul className="prose__list">
          {VARIANTS.map((variant) => (
            <li key={variant.name}>
              <div>
                <h3>{variant.name}</h3>
                <p>{variant.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <p>
          The host sets these before starting, and can change the grid and the line length
          independently — five squares wide needing only three in a row is a fast, vicious game worth
          trying at least once.
        </p>
      </section>

      <section className="prose__section">
        <h2>About the AI</h2>
        <p>
          Be warned: on the standard nine squares, <strong>the hard AI cannot be beaten</strong>. It
          is not bluffing and it is not being cruel — the game is small enough to solve completely, so
          it simply plays the whole thing out on every turn and takes a move that never loses. The
          best result available against it is a draw, and getting one is a genuine test.
        </p>
        <p>
          <strong>Medium</strong> plays the same way but slips about one move in ten, which is enough
          to lose a game that was drawn. <strong>Easy</strong> does not look ahead at all: it takes a
          win if one is sitting in front of it and misses roughly half the blocks it should make.
          That is the one to hand a child, or to warm up against.
        </p>
      </section>

      <section className="prose__section">
        <h2>Why the small grid draws</h2>
        <p>
          Noughts and crosses is <em>solved</em>, in the strict sense: every one of its positions has
          been worked out, and correct play from both sides always ends level. The centre is the
          strongest opening square, the corners are next, and the edges are where games are lost.
        </p>
        <p>
          That is not a reason to avoid it — it is the reason the variants above exist. Widen the
          board or invert the goal and the certainty disappears, because nobody has the whole thing
          memorised any more.
        </p>
      </section>

      <div className="prose__cta">
        <a className="btn btn--primary btn--large" {...linkProps(game.href)}>
          Play {game.name}
        </a>
      </div>
    </main>
  );
}
